import React, { useState, useEffect } from 'react';
import { Play, ArrowRight, CheckCircle, Loader, RotateCcw, Zap, X, MessageCircle } from 'lucide-react';

/**
 * Visual Flow Tab - Fancy diagram flow chart visualization
 * Shows animated A2A workflow with user,調度 Agent, and 機票 Agent
 * Enhanced with interactive message badges on agent avatars
 */
const VisualFlowTab = ({ a2aClient, isRunning, setIsRunning }) => {
  const [selectedWorkflow, setSelectedWorkflow] = useState('quiz');
  const [currentStep, setCurrentStep] = useState(0);
  const [messages, setMessages] = useState([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentMessages, setAgentMessages] = useState({});

  // Live AI Workflows
  const workflows = {
    learning: {
      id: 'learning',
      name: 'Basic Learning Request',
      description: 'Simple content generation workflow',
      difficulty: 'beginner',
      expectedTime: '20-40s',
      totalSteps: 6,
      steps: [
        { id: 1, from: 'user', to: 'dispatch', action: 'Request Learning Content', detail: 'User requests A2A learning material' },
        { id: 2, from: 'dispatch', to: 'content', action: 'Forward to Content Agent', detail: 'Dispatch agent routes to content generator' },
        { id: 3, from: 'content', to: 'ai', action: 'Generate Content', detail: 'DeepSeek AI creates educational content' },
        { id: 4, from: 'ai', to: 'content', action: 'Return AI Content', detail: 'Generated content returned' },
        { id: 5, from: 'content', to: 'dispatch', action: 'Package Response', detail: 'Content agent prepares response' },
        { id: 6, from: 'dispatch', to: 'user', action: 'Deliver Content', detail: 'User receives learning material' }
      ]
    },
    simulation: {
      id: 'simulation',
      name: 'Interactive Simulation',
      description: 'Running a capability discovery simulation',
      difficulty: 'intermediate',
      expectedTime: '30-50s',
      totalSteps: 7,
      steps: [
        { id: 1, from: 'user', to: 'dispatch', action: 'Request Simulation', detail: 'User wants A2A interaction demo' },
        { id: 2, from: 'dispatch', to: 'simulation', action: 'Forward to Simulation Agent', detail: 'Dispatch agent routes to simulator' },
        { id: 3, from: 'simulation', to: 'ai', action: 'Design Scenario', detail: 'DeepSeek designs realistic A2A scenario' },
        { id: 4, from: 'ai', to: 'simulation', action: 'Return Scenario', detail: 'AI provides simulation blueprint' },
        { id: 5, from: 'simulation', to: 'ai', action: 'Create Messages', detail: 'Generate agent message sequences' },
        { id: 6, from: 'ai', to: 'simulation', action: 'Return Messages', detail: 'Complete simulation ready' },
        { id: 7, from: 'dispatch', to: 'user', action: 'Deliver Simulation', detail: 'User receives interactive demo' }
      ]
    },
    quiz: {
      id: 'quiz',
      name: 'Quiz Assessment',
      description: 'Taking a quiz and receiving feedback',
      difficulty: 'beginner',
      expectedTime: '20-40s',
      totalSteps: 6,
      steps: [
        { id: 1, from: 'user', to: 'dispatch', action: 'Submit Quiz Answers', detail: 'Client submits quiz responses' },
        { id: 2, from: 'dispatch', to: 'assessment', action: 'Forward to Assessment Agent', detail: 'Dispatch routes to assessor' },
        { id: 3, from: 'assessment', to: 'ai', action: 'Analyze Understanding', detail: 'DeepSeek analyzes understanding' },
        { id: 4, from: 'ai', to: 'assessment', action: 'Return Analysis', detail: 'AI provides score and insights' },
        { id: 5, from: 'assessment', to: 'ai', action: 'Create Personalized Feedback', detail: 'AI creates personalized feedback' },
        { id: 6, from: 'dispatch', to: 'user', action: 'Deliver Results', detail: 'User receives feedback and score' }
      ]
    }
  };

  const currentWorkflow = workflows[selectedWorkflow];

  // Agent configurations with visual styling
  const agents = {
    user: {
      name: 'User',
      nameChinese: '用户',
      icon: '👤',
      color: 'from-blue-400 to-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-300',
      position: 'left'
    },
    dispatch: {
      name: 'Dispatch Agent',
      nameChinese: '调度 Agent',
      icon: '🤖',
      color: 'from-purple-400 to-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-300',
      position: 'center'
    },
    content: {
      name: 'Content Agent',
      nameChinese: '内容 Agent',
      icon: '📚',
      color: 'from-green-400 to-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-300',
      position: 'right'
    },
    simulation: {
      name: 'Simulation Agent',
      nameChinese: '模拟 Agent',
      icon: '🎮',
      color: 'from-orange-400 to-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-300',
      position: 'right'
    },
    assessment: {
      name: 'Assessment Agent',
      nameChinese: '评估 Agent',
      icon: '📝',
      color: 'from-pink-400 to-pink-600',
      bgColor: 'bg-pink-50',
      borderColor: 'border-pink-300',
      position: 'right'
    },
    ai: {
      name: 'DeepSeek AI',
      nameChinese: 'AI 引擎',
      icon: '🧠',
      color: 'from-yellow-400 to-yellow-600',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-300',
      position: 'far-right'
    }
  };

  // Timer effect
  useEffect(() => {
    let interval;
    if (isAnimating && startTime) {
      interval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isAnimating, startTime]);

  // Run workflow animation
  const runWorkflow = async () => {
    // Visual Flow tab works as an animated demonstration
    // It doesn't require actual backend calls - just visualization
    
    setIsAnimating(true);
    setIsRunning(true);
    setCurrentStep(0);
    setMessages([]);
    setStartTime(Date.now());

    const steps = currentWorkflow.steps;

    for (let i = 0; i < steps.length; i++) {
      setCurrentStep(i + 1);
      const step = steps[i];
      
      // Add message to log
      const newMessage = {
        id: step.id,
        from: step.from,
        to: step.to,
        action: step.action,
        detail: step.detail,
        timestamp: new Date().toLocaleTimeString(),
        status: 'processing'
      };
      
      setMessages(prev => [...prev, newMessage]);
      
      // Track messages per agent
      setAgentMessages(prev => ({
        ...prev,
        [step.from]: [...(prev[step.from] || []), { ...newMessage, direction: 'sent' }],
        [step.to]: [...(prev[step.to] || []), { ...newMessage, direction: 'received' }]
      }));

      // Simulate processing time
      await new Promise(resolve => setTimeout(resolve, 1500 + Math.random() * 1000));

      // Update message status
      setMessages(prev => prev.map(msg => 
        msg.id === step.id ? { ...msg, status: 'completed' } : msg
      ));
    }

    setIsAnimating(false);
    setIsRunning(false);
  };

  const resetWorkflow = () => {
    setCurrentStep(0);
    setMessages([]);
    setElapsedTime(0);
    setStartTime(null);
    setIsAnimating(false);
    setIsRunning(false);
    setAgentMessages({});
    setSelectedAgent(null);
  };

  // Get message count for an agent
  const getAgentMessageCount = (agentId) => {
    return agentMessages[agentId]?.length || 0;
  };

  // Get latest message for an agent
  const getAgentLatestMessage = (agentId) => {
    const msgs = agentMessages[agentId];
    return msgs && msgs.length > 0 ? msgs[msgs.length - 1] : null;
  };

  // Render agent avatar with interactive badge
  const AgentAvatar = ({ agentId, agent, isActive }) => {
    const messageCount = getAgentMessageCount(agentId);
    const hasMessages = messageCount > 0;
    const isSelected = selectedAgent === agentId;

    return (
      <div className="flex flex-col items-center space-y-3 z-10">
        <button
          onClick={() => hasMessages && setSelectedAgent(isSelected ? null : agentId)}
          className={`relative w-24 h-24 rounded-full bg-gradient-to-br ${agent.color} flex items-center justify-center shadow-lg transition-all ${
            isActive ? 'ring-4 ring-opacity-50 animate-pulse' : ''
          } ${
            isActive && agentId === 'user' ? 'ring-indigo-400' :
            isActive && agentId === 'dispatch' ? 'ring-purple-400' :
            isActive && (agentId === 'content' || agentId === 'simulation' || agentId === 'assessment') ? 'ring-green-400' :
            isActive && agentId === 'ai' ? 'ring-yellow-400' : ''
          } ${
            hasMessages ? 'cursor-pointer hover:scale-105' : ''
          } ${
            isSelected ? 'ring-4 ring-indigo-600 scale-110' : ''
          }`}
        >
          <span className="text-4xl">{agent.icon}</span>
          
          {/* Message Badge */}
          {hasMessages && (
            <div className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-xs font-bold shadow-lg animate-pulse">
              {messageCount}
            </div>
          )}
          
          {/* Message Icon Indicator */}
          {hasMessages && !isSelected && (
            <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1 shadow-md">
              <MessageCircle className="w-4 h-4 text-indigo-600" />
            </div>
          )}
        </button>
        <div className="text-center">
          <div className="font-bold text-gray-900">{agent.nameChinese}</div>
          <div className="text-xs text-gray-500">{agent.name}</div>
          {hasMessages && (
            <div className="text-xs text-indigo-600 font-medium mt-1">
              {messageCount} message{messageCount > 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card p-6 bg-gradient-to-r from-indigo-50 to-purple-50">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">A2A Workflow Visualization</h2>
            <p className="text-gray-600">See real AI agents in action with animated flow diagrams</p>
          </div>
          <div className="flex items-center space-x-2">
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              isAnimating ? 'bg-green-100 text-green-700 animate-pulse' : 'bg-gray-100 text-gray-600'
            }`}>
              <Zap className={`w-4 h-4 inline mr-1 ${isAnimating ? 'animate-pulse' : ''}`} />
              {isAnimating ? 'LIVE AI' : 'Ready'}
            </div>
          </div>
        </div>
      </div>

      {/* Workflow Selection */}
      <div className="card p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Select Live AI Workflow</h3>
        <div className="grid grid-cols-3 gap-4">
          {Object.values(workflows).map(workflow => (
            <button
              key={workflow.id}
              onClick={() => {
                if (!isAnimating) {
                  setSelectedWorkflow(workflow.id);
                  resetWorkflow();
                }
              }}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                selectedWorkflow === workflow.id
                  ? 'border-indigo-600 bg-indigo-50'
                  : 'border-gray-200 bg-white hover:border-indigo-300'
              } ${isAnimating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <div className="font-bold text-gray-900 mb-1">{workflow.name}</div>
              <div className="text-sm text-gray-600 mb-2">{workflow.description}</div>
              <div className="flex items-center justify-between text-xs">
                <span className={`badge ${
                  workflow.difficulty === 'beginner' ? 'bg-green-100 text-green-800' :
                  workflow.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {workflow.difficulty}
                </span>
                <span className="text-gray-500">
                  Expected: {workflow.expectedTime}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Main Visualization Panel */}
      <div className="card p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900">{currentWorkflow.name}</h3>
            <p className="text-sm text-gray-600">{currentWorkflow.description}</p>
          </div>
          <div className="flex items-center space-x-3">
            {isAnimating && (
              <div className="text-right">
                <div className="text-xs text-gray-500">Elapsed</div>
                <div className="text-lg font-bold text-indigo-600">{elapsedTime}s</div>
              </div>
            )}
            <button
              onClick={isAnimating ? resetWorkflow : runWorkflow}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${
                isAnimating
                  ? 'bg-gray-600 text-white hover:bg-gray-700'
                  : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 shadow-lg'
              }`}
            >
              {isAnimating ? (
                <>
                  <RotateCcw className="w-5 h-5" />
                  <span>Reset</span>
                </>
              ) : (
                <>
                  <Play className="w-5 h-5" />
                  <span>Start Live Workflow</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Progress</span>
            <span>{currentStep} / {currentWorkflow.totalSteps}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-indigo-600 to-purple-600 h-3 rounded-full transition-all duration-500"
              style={{ width: `${(currentStep / currentWorkflow.totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Agent Flow Diagram */}
        <div className="relative mb-12">
          {/* Title in Chinese */}
          <div className="text-center mb-8">
            <h4 className="text-2xl font-bold text-gray-800 mb-2">机票查询链路</h4>
            <p className="text-sm text-gray-500">Agent-to-Agent Communication Flow</p>
          </div>

          {/* Agents Row */}
          <div className="flex items-center justify-between relative">
            {/* User */}
            <AgentAvatar
              agentId="user"
              agent={agents.user}
              isActive={currentStep > 0 && (currentWorkflow.steps[currentStep - 1]?.from === 'user' || currentWorkflow.steps[currentStep - 1]?.to === 'user')}
            />

            {/* Arrow 1 */}
            <div className="flex-1 flex items-center justify-center">
              <div className={`flex items-center space-x-2 transition-all ${
                currentStep > 0 && (currentWorkflow.steps[currentStep - 1]?.from === 'user' || currentWorkflow.steps[currentStep - 1]?.to === 'dispatch')
                  ? 'opacity-100'
                  : 'opacity-30'
              }`}>
                <ArrowRight className={`w-8 h-8 text-indigo-600 ${currentStep > 0 ? 'animate-bounce-right' : ''}`} />
              </div>
            </div>

            {/* Dispatch Agent (調度 Agent) */}
            <AgentAvatar
              agentId="dispatch"
              agent={agents.dispatch}
              isActive={currentStep > 0 && (currentWorkflow.steps[currentStep - 1]?.from === 'dispatch' || currentWorkflow.steps[currentStep - 1]?.to === 'dispatch')}
            />

            {/* Arrow 2 */}
            <div className="flex-1 flex items-center justify-center">
              <div className={`flex items-center space-x-2 transition-all ${
                currentStep > 1 ? 'opacity-100' : 'opacity-30'
              }`}>
                <ArrowRight className={`w-8 h-8 text-purple-600 ${currentStep > 1 ? 'animate-bounce-right' : ''}`} />
              </div>
            </div>

            {/* Content/Simulation/Assessment Agent */}
            {selectedWorkflow === 'learning' && (
              <AgentAvatar
                agentId="content"
                agent={agents.content}
                isActive={currentStep > 1 && (currentWorkflow.steps[currentStep - 1]?.from === 'content' || currentWorkflow.steps[currentStep - 1]?.to === 'content')}
              />
            )}
            {selectedWorkflow === 'simulation' && (
              <AgentAvatar
                agentId="simulation"
                agent={agents.simulation}
                isActive={currentStep > 1 && (currentWorkflow.steps[currentStep - 1]?.from === 'simulation' || currentWorkflow.steps[currentStep - 1]?.to === 'simulation')}
              />
            )}
            {selectedWorkflow === 'quiz' && (
              <AgentAvatar
                agentId="assessment"
                agent={agents.assessment}
                isActive={currentStep > 1 && (currentWorkflow.steps[currentStep - 1]?.from === 'assessment' || currentWorkflow.steps[currentStep - 1]?.to === 'assessment')}
              />
            )}

            {/* Return Arrow */}
            <div className="flex-1 flex items-center justify-center">
              <div className={`flex items-center space-x-2 transition-all ${
                currentStep > 3 ? 'opacity-100' : 'opacity-30'
              }`}>
                <ArrowRight className={`w-8 h-8 text-gray-400 rotate-180 ${currentStep > 3 ? 'animate-bounce-left' : ''}`} />
              </div>
            </div>
          </div>

          {/* Current Action Display */}
          {currentStep > 0 && currentStep <= currentWorkflow.steps.length && (
            <div className="mt-12 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border-2 border-indigo-200">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <Loader className="w-6 h-6 text-indigo-600 animate-spin" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <span className="text-2xl">{agents[currentWorkflow.steps[currentStep - 1].from].icon}</span>
                    <ArrowRight className="w-5 h-5 text-gray-400" />
                    <span className="text-2xl">{agents[currentWorkflow.steps[currentStep - 1].to].icon}</span>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-1">
                    {currentWorkflow.steps[currentStep - 1].action}
                  </h4>
                  <p className="text-gray-600">
                    {currentWorkflow.steps[currentStep - 1].detail}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Agent Message Popup Modal */}
          {selectedAgent && agentMessages[selectedAgent] && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn" onClick={() => setSelectedAgent(null)}>
              <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                {/* Modal Header */}
                <div className={`p-6 bg-gradient-to-r ${agents[selectedAgent].color} text-white`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <span className="text-5xl">{agents[selectedAgent].icon}</span>
                      <div>
                        <h3 className="text-2xl font-bold">{agents[selectedAgent].nameChinese}</h3>
                        <p className="text-white text-opacity-90">{agents[selectedAgent].name}</p>
                        <p className="text-sm text-white text-opacity-80 mt-1">
                          {agentMessages[selectedAgent].length} message{agentMessages[selectedAgent].length > 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedAgent(null)}
                      className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full p-2 transition-all"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>
                </div>

                {/* Modal Body - Messages */}
                <div className="p-6 max-h-96 overflow-y-auto">
                  <div className="space-y-3">
                    {agentMessages[selectedAgent].map((msg, idx) => (
                      <div 
                        key={idx}
                        className={`p-4 rounded-lg border-l-4 ${
                          msg.direction === 'sent' 
                            ? 'bg-blue-50 border-blue-500' 
                            : 'bg-green-50 border-green-500'
                        } animate-fadeIn`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span className="text-lg">
                              {msg.direction === 'sent' ? agents[msg.from].icon : agents[msg.to].icon}
                            </span>
                            {msg.direction === 'sent' ? (
                              <>
                                <ArrowRight className="w-4 h-4 text-gray-400" />
                                <span className="text-lg">{agents[msg.to].icon}</span>
                              </>
                            ) : (
                              <>
                                <span className="text-lg">{agents[msg.from].icon}</span>
                                <ArrowRight className="w-4 h-4 text-gray-400" />
                              </>
                            )}
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                            msg.direction === 'sent' 
                              ? 'bg-blue-100 text-blue-700' 
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {msg.direction === 'sent' ? 'Sent' : 'Received'}
                          </span>
                        </div>
                        <h4 className="font-bold text-gray-900 mb-1">{msg.action}</h4>
                        <p className="text-sm text-gray-600 mb-2">{msg.detail}</p>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>{msg.timestamp}</span>
                          <div className="flex items-center space-x-1">
                            {msg.status === 'completed' ? (
                              <>
                                <CheckCircle className="w-3 h-3 text-green-600" />
                                <span className="text-green-600">Completed</span>
                              </>
                            ) : (
                              <>
                                <Loader className="w-3 h-3 text-yellow-600 animate-spin" />
                                <span className="text-yellow-600">Processing</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-end">
                  <button
                    onClick={() => setSelectedAgent(null)}
                    className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Message Log */}
        <div className="mt-8">
          <h4 className="font-bold text-gray-900 mb-4 flex items-center">
            <span className="mr-2">📋</span>
            Live Communication Log
          </h4>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {messages.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                Click "Start Live Workflow" to see agent communication in real-time
              </div>
            ) : (
              messages.map((msg, idx) => (
                <div 
                  key={idx}
                  className={`flex items-start space-x-3 p-3 rounded-lg border transition-all ${
                    msg.status === 'completed' 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-yellow-50 border-yellow-200 animate-pulse'
                  }`}
                >
                  <div className="flex-shrink-0">
                    {msg.status === 'completed' ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <Loader className="w-5 h-5 text-yellow-600 animate-spin" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-sm">{agents[msg.from].icon}</span>
                      <ArrowRight className="w-3 h-3 text-gray-400" />
                      <span className="text-sm">{agents[msg.to].icon}</span>
                      <span className="font-medium text-gray-900 text-sm">{msg.action}</span>
                    </div>
                    <p className="text-xs text-gray-600">{msg.detail}</p>
                    <span className="text-xs text-gray-400">{msg.timestamp}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Completion Message */}
      {currentStep === currentWorkflow.totalSteps && !isAnimating && (
        <div className="card p-6 bg-green-50 border-green-200 animate-fadeIn">
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-8 h-8 text-green-600" />
            <div>
              <h3 className="font-bold text-gray-900 text-lg">Workflow Completed! 🎉</h3>
              <p className="text-gray-600 mt-1">
                All {currentWorkflow.totalSteps} steps executed successfully in {elapsedTime} seconds.
                This demonstrates real A2A agent communication.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisualFlowTab;

